import { useRef, useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import api, { apiErr, isPaywall } from "@/lib/api";
import { Mic, X, Loader2, Send, RotateCcw, Scale, MessageSquare, Zap } from "lucide-react";
import { toast } from "sonner";

const SR = typeof window !== "undefined" && (window.SpeechRecognition || window.webkitSpeechRecognition);

// Flux vocal direct pour l'ado : ton direct, énergique, orienté action.
export default function TeenVoice({ open, onClose }) {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const recRef = useRef(null);
  const [listening, setListening] = useState(false);
  const [text, setText] = useState("");
  const [phase, setPhase] = useState("record"); // record | loading | result
  const [result, setResult] = useState(null);

  const stop = useCallback(() => {
    try { recRef.current?.stop(); } catch (_) { /* noop */ }
    setListening(false);
  }, []);

  useEffect(() => {
    if (!open) { stop(); setText(""); setResult(null); setPhase("record"); }
  }, [open, stop]);

  useEffect(() => () => { try { recRef.current?.stop(); } catch (_) { /* noop */ } }, []);

  const start = useCallback(() => {
    if (!SR) { toast.error(t("pages.teenVoice.noSR")); return; }
    let rec; try { rec = new SR(); } catch (_) { return; }
    rec.lang = i18n.language === "en" ? "en-US" : "fr-FR"; rec.continuous = true; rec.interimResults = true;
    const base = text;
    rec.onresult = (e) => {
      let f = "", it = "";
      for (let i = 0; i < e.results.length; i++) { const r = e.results[i]; (r.isFinal ? (f += r[0]?.transcript || "") : (it += r[0]?.transcript || "")); }
      setText((base ? base + " " : "") + (f + it).replace(/\s+/g, " ").trim());
    };
    rec.onerror = (ev) => {
      if (ev?.error === "not-allowed" || ev?.error === "service-not-allowed") toast.error(t("pages.teenVoice.micNotAllowed"));
      setListening(false);
    };
    rec.onend = () => setListening(false);
    recRef.current = rec;
    try { rec.start(); setListening(true); } catch (_) { /* noop */ }
  }, [text]);

  const send = async () => {
    const narrative = text.trim();
    if (narrative.length < 3) { toast.error(t("pages.teenVoice.sayMore")); return; }
    stop(); setPhase("loading");
    try {
      const { data } = await api.post("/ai/situation", { narrative, profile: "teen" });
      setResult(data); setPhase("result");
    } catch (e) {
      if (isPaywall(e)) toast.error(t("pages.teenVoice.limit"));
      else toast.error(apiErr(e));
      setPhase("record");
    }
  };

  const reset = () => { setText(""); setResult(null); setPhase("record"); };
  const go = (to) => { onClose?.(); navigate(to); };

  if (!open) return null;

  return (
    <AnimatePresence>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        data-testid="teen-voice" className="fixed inset-0 z-[60] flex flex-col p-5 overflow-y-auto"
        style={{ background: "linear-gradient(160deg,#09090B 45%,#06160c)" }}>
        <button data-testid="teen-voice-close" onClick={onClose} aria-label="Fermer"
          className="self-end w-11 h-11 rounded-full flex items-center justify-center"
          style={{ background: "#141417", color: "var(--accent)", border: "1px solid var(--surface-border)" }}>
          <X size={22} />
        </button>

        {phase === "record" && (
          <div className="flex-1 flex flex-col items-center justify-center text-center max-w-md mx-auto w-full">
            <h2 className="font-display text-4xl mb-2" style={{ color: "var(--text-main)" }}>{t("pages.teenVoice.talkToMe")}</h2>
            <p className="text-base mb-8" style={{ color: "color-mix(in srgb, var(--text-main) 65%, transparent)" }}>
              {listening ? t("pages.teenVoice.listening") : t("pages.teenVoice.pressMic")}
            </p>

            <motion.button data-testid="teen-voice-mic" onClick={() => (listening ? stop() : start())}
              animate={listening ? { scale: [1, 1.07, 1] } : { scale: 1 }}
              transition={listening ? { repeat: Infinity, duration: 1.1 } : {}}
              className="w-36 h-36 rounded-full flex items-center justify-center mb-8"
              style={{ background: listening ? "linear-gradient(135deg,#F87171,#EF4444)" : "var(--accent-gradient)",
                       boxShadow: "0 12px 40px -8px color-mix(in srgb, var(--accent) 55%, transparent)" }}>
              <Mic size={58} color={listening ? "#fff" : "var(--accent-fg)"} />
            </motion.button>
            {listening && <span className="text-sm mb-4" style={{ color: "var(--accent)" }}>{t("pages.teenVoice.listeningBadge")}</span>}

            {!!text && (
              <div data-testid="teen-voice-transcript" className="card-surface w-full p-5 text-left text-base mb-6" style={{ color: "var(--text-main)" }}>{text}</div>
            )}
            {!!text && !listening && (
              <div className="w-full flex flex-col gap-3">
                <button data-testid="teen-voice-send" onClick={send}
                  className="w-full rounded-2xl py-4 font-display text-lg flex items-center justify-center gap-2"
                  style={{ background: "var(--accent-gradient)", color: "var(--accent-fg)" }}>
                  <Send size={20} /> {t("pages.teenVoice.send")}
                </button>
                <button onClick={reset} className="w-full rounded-2xl py-3 font-display text-base flex items-center justify-center gap-2 card-surface" style={{ color: "var(--accent)" }}>
                  <RotateCcw size={17} /> {t("pages.teenVoice.retry")}
                </button>
              </div>
            )}
          </div>
        )}

        {phase === "loading" && (
          <div className="flex-1 flex flex-col items-center justify-center text-center">
            <Loader2 size={52} className="animate-spin mb-4" style={{ color: "var(--accent)" }} />
            <p className="font-display text-lg" style={{ color: "var(--text-main)" }}>{t("pages.teenVoice.thinking")}</p>
          </div>
        )}

        {phase === "result" && result && (
          <div className="flex-1 max-w-md mx-auto w-full pb-6">
            <div className="flex items-center gap-2 mb-4">
              <Zap size={24} style={{ color: "var(--accent)" }} />
              <h2 className="font-display text-2xl" style={{ color: "var(--text-main)" }}>{t("pages.teenVoice.received")}</h2>
            </div>
            {result.acknowledgement && (
              <div className="card-surface p-5 text-base mb-3" style={{ color: "var(--text-main)" }} data-testid="teen-voice-ack">{result.acknowledgement}</div>
            )}
            {result.reading && (
              <div className="card-surface p-5 text-base mb-3" style={{ color: "color-mix(in srgb, var(--text-main) 80%, transparent)" }}>{result.reading}</div>
            )}
            {result.reassurance && (
              <div className="rounded-2xl p-5 text-base mb-4 font-medium" style={{ background: "var(--accent-gradient)", color: "var(--accent-fg)" }}>{result.reassurance}</div>
            )}
            <div className="grid grid-cols-1 gap-3 mb-3">
              <button data-testid="teen-voice-rights" onClick={() => go("/rights")}
                className="rounded-2xl py-3.5 font-display flex items-center justify-center gap-2" style={{ background: "var(--accent-gradient)", color: "var(--accent-fg)" }}>
                <Scale size={18} /> {t("pages.teenVoice.rights")}
              </button>
              <button data-testid="teen-voice-reply" onClick={() => go("/reply")}
                className="rounded-2xl py-3.5 font-display flex items-center justify-center gap-2 card-surface" style={{ color: "var(--accent)" }}>
                <MessageSquare size={18} /> {t("pages.teenVoice.reply")}
              </button>
            </div>
            <button data-testid="teen-voice-again" onClick={reset}
              className="w-full rounded-2xl py-3 font-display flex items-center justify-center gap-2 card-surface" style={{ color: "color-mix(in srgb, var(--text-main) 70%, transparent)" }}>
              <RotateCcw size={17} /> {t("pages.teenVoice.again")}
            </button>
          </div>
        )}
      </motion.div>
    </AnimatePresence>
  );
}
